import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import VirtualList from '../components/VirtualList';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { Edit } from 'react-feather';
import { Button, ButtonGroup } from 'react-bootstrap';
import { showModal } from 'Core/redux/main/slice';
import { MODAL_TYPES } from 'Feature/components/app-modals';

const { AUXILIARY_EQUIPMENT_ADD_EDIT } = MODAL_TYPES;

const AuxiliaryEquipmentTile = () => {
  const [columnCount, setColumnCount] = useState(6);
  const [columnWidth, setColumnWidth] = useState(300);
  const auxiliaryEquipmentTileRef = useRef(null);
  const headerVisible = useSelector(isHeaderVisible);
  
  // Get data from Redux store
  const filteredData = useSelector(state => {
    const data = state.auxiliaryEquipment?.data;
    
    // Debug logging
    console.log('Auxiliary Equipment Tile - Redux State:', {
      data: data,
      dataType: typeof data,
      isArray: Array.isArray(data),
      dataLength: Array.isArray(data) ? data.length : 'not array',
      fullState: state.auxiliaryEquipment
    });
    
    // Ensure data is always an array
    if (!Array.isArray(data)) {
      console.log('Auxiliary Equipment Tile - Data is not array, returning empty array');
      return [];
    }
    
    const searchText = state.auxiliaryEquipment?.filters?.searchText || '';
    
    if (!searchText) return data;
    
    const lowerSearchText = searchText.toLowerCase();
    return data.filter(item => 
      (item.brand && item.brand.toLowerCase().includes(lowerSearchText)) ||
      (item.assetNumber && item.assetNumber.toLowerCase().includes(lowerSearchText)) ||
      (item.cellNumber && item.cellNumber.toLowerCase().includes(lowerSearchText)) ||
      (item.lane && item.lane.toLowerCase().includes(lowerSearchText))
    );
  });
  
  const isLoading = useSelector(state => state.auxiliaryEquipment?.loading || false);

  const updateColumnSettings = useCallback(() => {
    if (auxiliaryEquipmentTileRef.current) {
      const width = auxiliaryEquipmentTileRef.current.offsetWidth;
      let columns = 6;

      if (width < 576) {
        columns = 1; // Mobile
      } else if (width < 768) {
        columns = 3; // Tablets
      } else if (width < 992) {
        columns = 5; // Large screens
      } else {
        columns = 6; // Extra large screens
      }

      setColumnCount(columns);
      setColumnWidth(width / columns);
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = Array.isArray(filteredData) ? filteredData.map(auxiliaryEquipment => <Tile key={auxiliaryEquipment.id} auxiliaryEquipment={auxiliaryEquipment} />) : [];

  return (
    <div
      className={`shipment-tile-body ${!headerVisible ? 'header-collapsed' : ''}`}
      ref={auxiliaryEquipmentTileRef}
    >
      {isLoading && <LoadingSpinner />}
      <VirtualList
        columnCount={columnCount}
        rowCount={Math.ceil((Array.isArray(filteredData) ? filteredData.length : 0) / columnCount)}
        itemData={itemData}
        columnWidth={columnWidth}
        rowHeight={150}
      />
    </div>
  );
};

const Tile = ({ auxiliaryEquipment }) => {
  const dispatch = useDispatch();

  const handleEdit = e => {
    e.stopPropagation();
    console.log('Opening modal with auxiliaryEquipment:', auxiliaryEquipment);
    console.log('Auxiliary Equipment ID:', auxiliaryEquipment?.id);
    console.log('Auxiliary Equipment auxiliaryEquipmentId:', auxiliaryEquipment?.auxiliaryEquipmentId);
    dispatch(showModal(AUXILIARY_EQUIPMENT_ADD_EDIT, auxiliaryEquipment));
  };

  return (
    <div key={auxiliaryEquipment.id} className="shipment-tile ellipsis-tile">
      <div
        className={`card p-3 m-2 ${auxiliaryEquipment.isNewlyCreated ? 'border-success' : ''}`}
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button variant="light" onClick={handleEdit}>
              <Edit size={16} />
            </Button>
          </ButtonGroup>
        </div>
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-row align-items-center">
            <div className="ms-2 c-details">
              <h6 className="mb-0 truncate-2lines" title={auxiliaryEquipment.brand}>
                {auxiliaryEquipment.brand}
              </h6>
              <span className="text-muted fa-sm">Tonnage: {auxiliaryEquipment.tonnage || '--'}</span>
            </div>
          </div>
        </div>
        <div className="ms-2">
          <div className="mt-2 d-flex flex-column justify-content-center" style={{ lineHeight: '1rem' }}>
            <h6 className="heading mb-2">Asset#: {auxiliaryEquipment.assetNumber || '--'}</h6>
            <h6 className="border-top pt-2 heading">Cell: {auxiliaryEquipment.cellNumber}</h6>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuxiliaryEquipmentTile;
